import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useLocales } from '@/features/locales/hooks';

const STORAGE_KEY = 'dp.contentLocale';

interface ContentLocaleValue {
  /** Locale code the admin is currently reading content in. */
  code: string | null;
  /** Locale id for the same locale, or null until the locale list loads. */
  id: number | null;
  setCode: (code: string) => void;
}

const ContentLocaleContext = createContext<ContentLocaleValue | null>(null);

/**
 * Which locale the admin *reads* content in.
 *
 * Distinct from any per-record editing locale: this only decides which
 * translation the browse screens show, so a content list is one language at a
 * time instead of whichever translation each row happened to be stored with.
 * Persisted so a reload does not silently switch the language back.
 */
export function ContentLocaleProvider({ children }: { children: ReactNode }) {
  const localesQuery = useLocales();
  const [code, setCodeState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const locales = useMemo(() => localesQuery.data ?? [], [localesQuery.data]);

  // Fall back to the default locale once the list arrives — and drop a stored
  // code that no longer exists (locale deleted or deactivated).
  useEffect(() => {
    if (locales.length === 0) return;

    const known = locales.some((locale) => locale.code === code);
    if (known) return;

    const fallback = locales.find((locale) => locale.is_default)?.code ?? locales[0]?.code ?? null;
    setCodeState(fallback);
  }, [locales, code]);

  const value = useMemo<ContentLocaleValue>(() => {
    const match = locales.find((locale) => locale.code === code);

    return {
      code: match?.code ?? null,
      id: match?.id ?? null,
      setCode: (next: string) => {
        setCodeState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // Private mode / storage disabled — the selection just won't persist.
        }
      },
    };
  }, [locales, code]);

  return <ContentLocaleContext.Provider value={value}>{children}</ContentLocaleContext.Provider>;
}

export function useContentLocale(): ContentLocaleValue {
  const value = useContext(ContentLocaleContext);

  if (value === null) {
    throw new Error('useContentLocale must be used inside a ContentLocaleProvider');
  }

  return value;
}
